import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Camera, Scissors, Hammer, Dumbbell, CalendarCheck, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import DemoModal from '../components/DemoModal';
import AiEmployeeHeroVideoCard from '../components/AiEmployeeHeroVideoCard';

// Testimonials for the AI Employee page — each links (in-app, same tab, via
// React Router <Link>, never target="_blank") to the existing product page
// most relevant to what that business owner used.
const aiEmployeeTestimonials = [
  {
    category: 'Photographers',
    company: 'Everly Lane Photography',
    name: 'Priya Nandan, Owner',
    icon: Camera,
    iconColor: '#E8A0B8',
    quote: 'Booking calls used to eat my evenings. Now GoogLyn handles scheduling and client questions while I\u2019m shooting weddings.',
    href: '/product/ai-concierge',
  },
  {
    category: 'Salons & Barbershops',
    company: 'The Fade District',
    name: 'Marcus Ellery, Owner',
    icon: Scissors,
    iconColor: '#C4A0B8',
    quote: 'We built our booking app in an afternoon, no developer needed. Clients rebook themselves and our chairs stay full.',
    href: '/product/ai-app-builder',
  },
  {
    category: 'Contractors & Renovators',
    company: 'Hallwright Builders',
    name: 'Tom Hallwright, Owner',
    icon: Hammer,
    iconColor: '#4A7C9D',
    quote: 'Broadcast-ready ads got our crew in front of homeowners across the city \u2014 leads started calling within days.',
    href: '/product/broadcast-commercials',
  },
  {
    category: 'Personal Trainers & Coaches',
    company: 'Ironline Coaching',
    name: 'Dana Okafor, Founder',
    icon: Dumbbell,
    iconColor: '#E8A05C',
    quote: 'GoogLyn answers client questions about programs and pricing 24/7, so I can focus on coaching instead of my phone.',
    href: '/product/ai-concierge',
  },
  {
    category: 'Event Planners',
    company: 'Marlowe & Co. Events',
    name: 'Sofia Marlowe, Lead Planner',
    icon: CalendarCheck,
    iconColor: '#B87FA0',
    quote: 'One platform now runs our inquiries, quotes, and client records \u2014 no more juggling five different tools per event.',
    href: '/product/enterprise-solutions',
  },
];

const BUSINESS_TYPES = [
  'Photographers', 'Salons & Barbershops', 'Contractors & Renovators',
  'Personal Trainers & Coaches', 'Real Estate Agents', 'Event Planners',
  'Cleaning Services', 'Auto Detailers', 'Consultants', 'Tutors',
  'Pet Groomers', 'Med Spas', 'Bakeries & Caterers', 'Landscapers',
];

const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const AiEmployee = () => {
  const [businessType, setBusinessType] = useState('');
  const [demoOpen, setDemoOpen] = useState(false);
  const [prefillMessage, setPrefillMessage] = useState('');

  // Opens the existing Book a Demo modal with the typed/selected business
  // type pre-filled into the "tell us about your use case" field. This is
  // purely a styled lead-in — no live demo/storefront is generated.
  const openDemoFor = (type) => {
    const value = (type !== undefined ? type : businessType).trim();
    setPrefillMessage(
      value
        ? `I run a ${value} business. I'd like to see how AI Employee could handle my customers, quotes, and bookings.`
        : ''
    );
    setDemoOpen(true);
  };

  const handlePillClick = (type) => {
    setBusinessType(type);
    openDemoFor(type);
  };

  return (
    <div className="w-full">
      {/* Section 1: Two-column hero — heading + subtext on the left, rounded
          Cloudflare Stream video card on the right (stacks on mobile). Mirrors
          the "Enterprise AI In Action" reference styling (rounded-2xl 16:9
          card, soft shadow, bottom-right play/pause + mute + fullscreen). */}
      <section className="bg-white" data-testid="ai-employee-video-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-medium text-black/90 mb-4 sm:mb-6"
                data-testid="ai-employee-hero-heading"
              >
                One Elegant System. Every Tool Your Business Needs.
              </h1>
              <p
                className="text-base sm:text-lg text-black/70 max-w-2xl mx-auto lg:mx-0"
                data-testid="ai-employee-hero-subhead"
              >
                Stop juggling five disconnected apps. AI Employee brings your website, bookings,
                payments, customer records, and AI-powered operations into a single, considered platform.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <AiEmployeeHeroVideoCard cloudflareVideoId="f8df1af676a2a7aa4cd223188981da92" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: interactive teaser (previously merged with a duplicate
          hero heading — that heading now lives in the hero above). */}
      <section className="min-h-[85vh] flex items-center justify-center" style={{ backgroundColor: '#F5F7F5' }}>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2
              className="text-3xl md:text-4xl font-medium text-black/90 mb-4 sm:mb-6"
              data-testid="ai-employee-teaser-heading"
            >
              See AI Employee, Built Around Your Business.
            </h2>
            <p
              className="text-base sm:text-lg text-black/70 max-w-2xl mx-auto mb-8 sm:mb-10"
              data-testid="ai-employee-teaser-subhead"
            >
              Tell us what you do, and see how AI Employee would handle your customers, quotes,
              and bookings — tailored to your industry.
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); openDemoFor(); }}
              className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto mb-8 sm:mb-10"
            >
              <label htmlFor="ai-employee-business-input" className="sr-only">
                What kind of business do you run?
              </label>
              <Input
                id="ai-employee-business-input"
                placeholder="What kind of business do you run?"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                className="flex-1 h-11 bg-white border-black/10 text-black placeholder:text-black/40"
                data-testid="ai-employee-business-input"
              />
              <Button
                type="submit"
                className="h-11 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105 px-6"
                data-testid="ai-employee-cta-button"
              >
                See It In Action
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>

            <div className="flex flex-wrap justify-center gap-2 sm:gap-3" data-testid="ai-employee-pill-list">
              {BUSINESS_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handlePillClick(type)}
                  className="px-4 py-2 text-sm rounded-full border border-[#E8A0B8]/40 text-black/70 bg-white hover:bg-[#FDF7F9] hover:border-[#E8A0B8] hover:text-[#E8A0B8] transition-colors duration-200"
                  data-testid={`ai-employee-pill-${slugify(type)}`}
                >
                  {type}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials — click-through navigates in-app via <Link> (same tab,
          never a new window/tab) to the most relevant existing product page. */}
      <section className="py-16 md:py-24 overflow-hidden" style={{ backgroundColor: '#E3E8E0' }}>
        <div className="text-center mb-10 md:mb-14 px-4">
          <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-3 sm:mb-4" data-testid="ai-employee-testimonials-heading">
            Real Businesses, Real Results
          </h2>
          <p className="text-base sm:text-lg text-black/70 max-w-2xl mx-auto">
            See how business owners like you are using GoogLyn AI to run smarter operations.
          </p>
        </div>
        <div className="relative">
          <div className="marquee-wrapper">
            <div className="marquee-content" data-testid="ai-employee-testimonials-marquee">
              {[...aiEmployeeTestimonials, ...aiEmployeeTestimonials].map((testimonial, idx) => (
                <div key={`ai-employee-testimonial-${idx}`} className="marquee-card" data-testid="ai-employee-testimonial-card">
                  <Link
                    to={testimonial.href}
                    className="block h-full"
                    data-testid={`ai-employee-testimonial-link-${idx}`}
                  >
                    <Card
                      className="h-full bg-white/95 backdrop-blur-sm border border-white/60 transition-transform duration-300 ease-out hover:scale-105"
                      style={{
                        boxShadow: '0 16px 32px rgba(0, 0, 0, 0.06), 0 0 1px rgba(0,0,0,0.1)',
                        borderRadius: '16px',
                        width: '320px',
                        minWidth: '320px',
                      }}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between mb-2">
                          <Badge
                            variant="outline"
                            className="w-fit bg-gradient-to-r from-[#FDF7F9] to-[#F4C2D4]/30 text-[#E8A0B8] border-none px-3 py-1 text-xs font-medium"
                            style={{ borderRadius: '20px' }}
                          >
                            {testimonial.category}
                          </Badge>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className="w-3.5 h-3.5 text-[#E8A0B8]" fill="#E8A0B8" />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              background: `linear-gradient(135deg, ${testimonial.iconColor}20, ${testimonial.iconColor}10)`,
                              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.06)',
                            }}
                          >
                            <testimonial.icon className="w-5 h-5" style={{ color: testimonial.iconColor }} strokeWidth={2.5} />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-black/90 text-lg font-medium">
                              {testimonial.company}
                            </CardTitle>
                            <p className="text-xs text-black/50 mt-0.5">{testimonial.name}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <blockquote className="text-black/75 italic text-sm leading-relaxed pl-3 relative">
                          <div
                            className="absolute left-0 top-0 bottom-0 w-0.5 rounded-full"
                            style={{ background: `linear-gradient(to bottom, ${testimonial.iconColor}, ${testimonial.iconColor}50)` }}
                          />
                          {'\u201C'}{testimonial.quote}{'\u201D'}
                        </blockquote>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} prefillMessage={prefillMessage} />
    </div>
  );
};

export default AiEmployee;
